import { ArrowLeft } from "@gravity-ui/icons";
import {
  Alert,
  Button,
  Flex,
  Icon,
  Loader,
  Pagination,
  Text,
} from "@gravity-ui/uikit";
import { ProductServiceType } from "definitions/exporter";
import { FormProvider, useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { useGravityDevice } from "src/hooks/useGravityDevice";
import { RoutesPaths } from "src/routes/RoutesPaths";

import { ExportTasksForm } from "../ExportTasksForm";
import { ExportTaskFormValues } from "../ExportTasksForm/ExportTasksForm.types";
import { ResponsiveExportLogsWidget } from "../ResponsiveExportLogsWidget";
import { JobsTable } from "./components";
import css from "./ExportTaskWidget.module.scss";
import { useExportTaskWidget } from "./useExportTaskWidget";

const DEFAULT_VALUE: Partial<ExportTaskFormValues> = {
  transformers: [],
  excludeTradeTypes: [95],
  forceUpdate: true,
  priceOptions: [],
  hoursSinceLastUpdate: 0,
  cronTask: undefined,
  platform: ProductServiceType.Poizon,
  collapseVariantsByProductId: undefined,
};

const SKELETON_ROWS = [0, 1, 2, 3, 4];

export const ExportTaskWidget = () => {
  const form = useForm<ExportTaskFormValues>({
    defaultValues: DEFAULT_VALUE,
  });

  const { isDesktop } = useGravityDevice();
  const { taskId } = useParams();
  const navigate = useNavigate();

  const { jobs, isJobsLoading, page, pageSize, onPageChange } =
    useExportTaskWidget(taskId);

  if (!taskId) {
    return <Alert title="Ошибка данных" message="Не указан ID выгрузки" />;
  }

  // Раньше всю grid-разметку заменял page-level skeleton по
  // isInitialLoading = !hasJobs && isProductSamplesLoading. Этот флаг шёл по
  // циклу false → true → false на маунте (samples-fetch стартует не сразу),
  // из-за чего ExportTasksForm монтилась → анмонтилась → монтилась за 200 мс,
  // дважды дёргая getExportTaskOptions + цепочку paramKeys/paramValues. Форма
  // и так показывает empty fields ~200 мс до прихода данных, а jobs-секция
  // ниже сама умеет рисовать skeleton для пустого initial-state.
  return (
    <Flex direction={"column"} gap={4}>
      <Flex>
        <Button view="flat" onClick={() => navigate(RoutesPaths.LK.EXPORT)}>
          <Icon data={ArrowLeft} />
          Назад
        </Button>
      </Flex>
      <ResponsiveExportLogsWidget />
      <div
        className={`${css.layout} ${isDesktop ? css.layoutDesktop : css.layoutMobile}`}
      >
        <FormProvider {...form}>
          <ExportTasksForm taskId={taskId} />
        </FormProvider>
        <Flex
          direction={"column"}
          gap={4}
          width={"100%"}
          className={css.jobsSection}
        >
          <div className={css.jobsSectionHeader}>
            <Text variant="header-1">История задач</Text>
          </div>
          {isJobsLoading && jobs.data.length === 0 ? (
            <Flex direction={"column"} gap={2}>
              {SKELETON_ROWS.map((i) => (
                <Skeleton
                  key={i}
                  width={"100%"}
                  height={"48px"}
                  baseColor="var(--g-color-base-generic)"
                  highlightColor="var(--g-color-base-simple-hover)"
                />
              ))}
            </Flex>
          ) : jobs.totalItems > 0 ? (
            <>
              <div className={css.jobsTableContainer}>
                <JobsTable jobs={jobs.data} />
              </div>
              <div className={css.jobsPagination}>
                {jobs.totalItems > pageSize && (
                  <Pagination
                    showPages
                    page={page}
                    pageSize={pageSize}
                    total={jobs.totalItems}
                    onUpdate={onPageChange}
                  />
                )}
                {isJobsLoading && <Loader size="s" />}
              </div>
            </>
          ) : (
            <Alert theme="info" title={"Нет истории задач"} />
          )}
        </Flex>
      </div>
    </Flex>
  );
};
